import {
  TextField,
  Select,
  Text,
  Card,
  InlineGrid,
  Checkbox,
} from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { CustomField } from "types/CustomField";
import { Product } from "types/Product";
import DatePickerx from "features/datepicker/date-picker";
import ProductsSelect from "components/products-select/ProductsSelect";
import ProductGroupSelect from "components/products-select/ProductGroupSelect";
import ProductsAndGroupsSelect from "components/products-select/ProductsAndGroupsSelect";
import AddressSelectMultiple from "features/customer-address-select-multiple/customer-address-select-multiple";
import UserSelect from "features/user-select/user-select";
import AddressCreateForm from "containers/address-create/address-create";
import DataTable from "components/datatable/Datatable";
import CustomFieldFileUpload from "components/custom-field-file-upload/custom-field-file-upload";
import { getAllCountryOptions } from "constants/countries";
import {
  getStringValue,
  isCustomFieldShown,
  sanitizeNumberInput,
} from "../build-pdf-utils";
import BusinessTripCustomFieldSelect, {
  BusinessTripFilterContext,
} from "./BusinessTripCustomFieldSelect";

interface CustomFieldsRendererProps {
  customFields: CustomField[];
  errors: { [key: string]: string };
  onFieldChange: (index: number, value: string | string[]) => void;
  products: Product[];
  productGroups: any[];
  setProducts: (p: Product[]) => void;
  setProductGroups: (pg: any[]) => void;
  orderId: string | null;
  templateId: string;
  customerId: string;
  fileEntitiesMap: { [key: string]: any[] };
  businessTripFilterContext?: BusinessTripFilterContext;
}

export default function CustomFieldsRenderer({
  customFields,
  errors,
  onFieldChange,
  products,
  productGroups,
  setProducts,
  setProductGroups,
  orderId,
  templateId,
  customerId,
  fileEntitiesMap,
  businessTripFilterContext,
}: CustomFieldsRendererProps) {
  const { t } = useTranslation();

  return (
    <>
      {customFields.map((field, index) => (
        <div key={field.id} hidden={!isCustomFieldShown(field, customFields)}>
          {field.type === "string" && (
            <TextField
              label={field.name}
              value={getStringValue(field)}
              autoComplete="off"
              onChange={(value: string) => onFieldChange(index, value)}
              requiredIndicator={field.required}
              error={errors[field.label]}
              helpText={field.helperText}
            />
          )}
          {field.type === "number" && (
            <TextField
              label={field.name}
              value={getStringValue(field)}
              autoComplete="off"
              onChange={(value: string) =>
                onFieldChange(index, sanitizeNumberInput(value))
              }
              requiredIndicator={field.required}
              error={errors[field.label]}
            />
          )}
          {field.type === "textarea" && (
            <TextField
              label={field.name}
              value={getStringValue(field)}
              multiline={4}
              autoComplete="off"
              onChange={(value: string) => onFieldChange(index, value)}
              requiredIndicator={field.required}
              error={errors[field.label]}
              helpText={field.helperText}
            />
          )}
          {field.type === "date" && (
            <DatePickerx
              disableDatesAfter={null}
              title={field.name}
              isRequired={field.required}
              onChange={(value: any) =>
                onFieldChange(index, value.toISOString().split("T")[0])
              }
              selected={getStringValue(field)}
            />
          )}
          {field.type === "select" && (
            <Select
              label={field.name}
              options={[
                {
                  label: t(
                    "files_template_custom_type_select_select_an_option"
                  ),
                  value: "",
                },
                ...field.options.map((option) => ({
                  label: option,
                  value: option,
                })),
              ]}
              value={getStringValue(field)}
              onChange={(value: string) => onFieldChange(index, value)}
              requiredIndicator={field.required}
              error={errors[field.label]}
            />
          )}
          {field.type === "products" && !orderId && (
            <ProductsSelect
              onChange={(value: any) => {
                if (JSON.stringify(products) !== JSON.stringify(value)) {
                  onFieldChange(index, value);
                  setProducts(value);
                }
              }}
              initialProducts={products}
              isRequired={field.required}
            />
          )}
          {field.type === "product_group" && !orderId && (
            <ProductGroupSelect
              onProductGroupsChange={(groups: any[]) => {
                if (
                  JSON.stringify(productGroups) !== JSON.stringify(groups)
                ) {
                  onFieldChange(index, groups as any);
                  setProductGroups(groups);
                }
              }}
              initialProductGroups={productGroups}
              isRequired={field.required}
            />
          )}
          {field.type === "products_or_groups" && !orderId && (
            <ProductsAndGroupsSelect
              onChange={(data: {
                products: Product[];
                productGroups: any[];
              }) => {
                const currentData = { products, productGroups };
                if (JSON.stringify(currentData) !== JSON.stringify(data)) {
                  setProducts(data.products);
                  setProductGroups(data.productGroups);
                  onFieldChange(index, JSON.stringify(data) as any);
                }
              }}
              initialProducts={products}
              initialProductGroups={productGroups}
              isRequired={field.required}
              enableGroupOption={true}
              error={errors[field.label]}
            />
          )}
          {field.type === "products" &&
            templateId &&
            orderId &&
            products.length > 0 && (
              <Card roundedAbove="sm">
                <InlineGrid columns="1fr auto">
                  <Text as="h2" variant="headingSm">
                    {t("products")}
                  </Text>
                </InlineGrid>
                <DataTable
                  collection={products}
                  columns={[
                    { key: "title", name: t("name") },
                    {
                      key: "quantity",
                      isDisabledTotal: true,
                      type: "number",
                      name: t("total_quantity"),
                    },
                    { key: "price", type: "currency", name: t("price") },
                    {
                      key: "finalPrice",
                      type: "currency",
                      name: t("total_amount"),
                    },
                  ]}
                />
              </Card>
            )}
          {field.type === "product_group" &&
            templateId &&
            orderId &&
            productGroups.length > 0 && (
              <Card roundedAbove="sm">
                <InlineGrid columns="1fr auto">
                  <Text as="h2" variant="headingSm">
                    {t("product_groups")}
                  </Text>
                </InlineGrid>
                <DataTable
                  collection={productGroups}
                  columns={[
                    { key: "code", name: t("code") },
                    { key: "title", name: t("product_group") },
                    {
                      key: "quantity",
                      isDisabledTotal: true,
                      type: "number",
                      name: t("total_quantity"),
                    },
                    {
                      key: "price",
                      type: "currency",
                      name: t("price"),
                    },
                    {
                      key: "finalPrice",
                      type: "currency",
                      name: t("total_amount"),
                    },
                  ]}
                />
              </Card>
            )}
          {field.type === "products_or_groups" &&
            templateId &&
            orderId &&
            (products.length > 0 || productGroups.length > 0) && (
              <>
                {products.length > 0 && (
                  <Card roundedAbove="sm">
                    <InlineGrid columns="1fr auto">
                      <Text as="h2" variant="headingSm">
                        {t("products")}
                      </Text>
                    </InlineGrid>
                    <DataTable
                      collection={products}
                      columns={[
                        { key: "title", name: t("name") },
                        {
                          key: "quantity",
                          isDisabledTotal: true,
                          type: "number",
                          name: t("total_quantity"),
                        },
                        {
                          key: "price",
                          type: "currency",
                          name: t("price"),
                        },
                        {
                          key: "finalPrice",
                          type: "currency",
                          name: t("total_amount"),
                        },
                      ]}
                    />
                  </Card>
                )}
                {productGroups.length > 0 && (
                  <Card roundedAbove="sm">
                    <InlineGrid columns="1fr auto">
                      <Text as="h2" variant="headingSm">
                        {t("product_groups")}
                      </Text>
                    </InlineGrid>
                    <DataTable
                      collection={productGroups}
                      columns={[
                        { key: "code", name: t("code") },
                        { key: "title", name: t("product_group") },
                        {
                          key: "quantity",
                          isDisabledTotal: true,
                          type: "number",
                          name: t("total_quantity"),
                        },
                        {
                          key: "price",
                          type: "currency",
                          name: t("price"),
                        },
                        {
                          key: "finalPrice",
                          type: "currency",
                          name: t("total_amount"),
                        },
                      ]}
                    />
                  </Card>
                )}
              </>
            )}
          {field.type === "address" && (
            <>
              <AddressCreateForm />
              <AddressSelectMultiple
                value={
                  Array.isArray(field.value)
                    ? field.value.filter(Boolean)
                    : field.value
                    ? [field.value]
                    : []
                }
                onChange={(value: any) => {
                  onFieldChange(index, value);
                }}
                customerId={customerId}
                isRequired={field.required}
              />
            </>
          )}
          {field.type === "user" && (
            <UserSelect
              onChange={(value: string) => onFieldChange(index, value)}
              value={getStringValue(field)}
              label={field.name}
            />
          )}
          {field.type === "signature" && (
            <UserSelect
              onChange={(value: string) => onFieldChange(index, value)}
              value={getStringValue(field)}
              label={field.name}
            />
          )}
          {field.type === "checkbox" && (
            <div>
              {field.options && field.options.length > 0 ? (
                <>
                  {field.name && (
                    <Text as="p" variant="bodyMd">
                      {field.name}
                    </Text>
                  )}
                  {field.options.map((option) => {
                    const currentValue = Array.isArray(field.value)
                      ? field.value
                      : field.value
                      ? [field.value]
                      : [];
                    const checked = currentValue.includes(option);
                    return (
                      <Checkbox
                        key={option}
                        label={option}
                        checked={checked}
                        onChange={(isChecked: boolean) => {
                          let newValue: string | string[];
                          if (field.isMulti) {
                            const base = currentValue;
                            if (isChecked) {
                              newValue = Array.from(
                                new Set([...base, option])
                              );
                            } else {
                              newValue = base.filter((v) => v !== option);
                            }
                          } else {
                            newValue = isChecked ? option : "";
                          }
                          onFieldChange(index, newValue);
                        }}
                      />
                    );
                  })}
                </>
              ) : (
                <Checkbox
                  label={field.name}
                  checked={!!field.value}
                  onChange={(isChecked: boolean) =>
                    onFieldChange(index, isChecked ? "1" : "")
                  }
                />
              )}
            </div>
          )}
          {field.type === "country" && (
            <Select
              label={field.name}
              options={[
                {
                  label: t(
                    "files_template_custom_type_select_select_an_option"
                  ),
                  value: "",
                },
                ...getAllCountryOptions(t),
              ]}
              value={getStringValue(field)}
              onChange={(value: string) => onFieldChange(index, value)}
              requiredIndicator={field.required}
              error={errors[field.label]}
            />
          )}
          {field.type === "business_trip" && (
            <BusinessTripCustomFieldSelect
              label={field.name}
              value={getStringValue(field)}
              required={field.required}
              error={errors[field.label]}
              context={businessTripFilterContext}
              onChange={(value: string) => onFieldChange(index, value)}
            />
          )}
          {field.type === "file" && (
            <CustomFieldFileUpload
              label={field.name}
              value={field.value}
              onChange={(fileIds: string[]) => onFieldChange(index, fileIds)}
              isRequired={field.required}
              error={errors[field.label]}
              existingFiles={(fileEntitiesMap[field.label] || []).filter(
                (file) => {
                  const currentFileIds = Array.isArray(field.value)
                    ? field.value
                    : field.value
                    ? [field.value]
                    : [];
                  return currentFileIds.includes(file.id);
                }
              )}
            />
          )}
        </div>
      ))}
    </>
  );
}
